<script setup lang="ts">
import { RouterLink } from 'vue-router';
import NavIcon from '@/components/NavIcon.vue';

/**
 * Action compacte : icône seule, nom de l'action en infobulle. L'infobulle
 * est en CSS (survol ET focus clavier) plutôt qu'un `title` natif, qui tarde
 * à s'afficher et n'apparaît jamais au clavier ; `aria-label` porte le nom
 * pour les lecteurs d'écran.
 *
 * Infobulle en `display: none` au repos (pas seulement transparente) et alignée
 * sur le bord droit : ces actions vivent dans la dernière colonne d'un tableau
 * à défilement horizontal, qu'une infobulle débordant à droite élargissait.
 */
defineProps<{
  label: string;
  icon: InstanceType<typeof NavIcon>['$props']['name'];
  to?: string;
  href?: string;
  download?: boolean;
  danger?: boolean;
  disabled?: boolean;
  /** `bottom` pour une action en haut de page, où une infobulle au-dessus sortirait de l'écran. */
  tooltip?: 'top' | 'bottom';
}>();
defineEmits<{ click: [] }>();

const base =
  'inline-flex h-9 w-9 !min-h-0 items-center justify-center rounded-lg border bg-surface transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pulse-400 disabled:cursor-not-allowed disabled:opacity-50';
</script>

<template>
  <span class="group relative inline-flex">
    <RouterLink v-if="to" :to="to" :aria-label="label" :class="[base, 'border-slate-300 text-ink-soft hover:bg-slate-100']">
      <NavIcon :name="icon" />
    </RouterLink>
    <a
      v-else-if="href"
      :href="href"
      :download="download || undefined"
      :aria-label="label"
      :class="[base, 'border-slate-300 text-ink-soft hover:bg-slate-100']"
    >
      <NavIcon :name="icon" />
    </a>
    <button
      v-else
      type="button"
      :aria-label="label"
      :disabled="disabled"
      :class="[base, danger ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-slate-300 text-ink-soft hover:bg-slate-100']"
      @click="$emit('click')"
    >
      <NavIcon :name="icon" />
    </button>
    <span
      role="tooltip"
      class="pointer-events-none absolute right-0 z-40 hidden whitespace-nowrap rounded-md bg-ink px-2 py-1 text-xs font-medium text-surface group-focus-within:block group-hover:block"
      :class="tooltip === 'bottom' ? 'top-full mt-1.5' : 'bottom-full mb-1.5'"
    >
      {{ label }}
    </span>
  </span>
</template>
